import React, { useEffect, useState } from "react";
import Select from "react-select";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import api from "../../../core/api/config";
import { reactSelectStyles } from "../../../shared/styles/reactSelectStyles";
import { maskCpf } from "../../../shared/utils/maskCpf";
import { validateCpfWithMessage } from "../../../shared/utils/validateCpf";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

const EditUserModal = ({ show, usuario, onClose, onUpdated }) => {
  const [formData, setFormData] = useState({});
  const [gruposOptions, setGruposOptions] = useState([]);
  const [gruposSelecionados, setGruposSelecionados] = useState([]);
  const [vinculados, setVinculados] = useState([]);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [loadingVinculos, setLoadingVinculos] = useState(false);
  const [cidadesOptions, setCidadesOptions] = useState([]);
  const [loadingCidades, setLoadingCidades] = useState(false);
  const [tiposAcessoOptions, setTiposAcessoOptions] = useState([]);
  const [loadingTiposAcesso, setLoadingTiposAcesso] = useState(false);
  const [cpfError, setCpfError] = useState("");
  const [usuarioLogado, setUsuarioLogado] = useState(null);
  const [perfis, setPerfis] = useState([]);
  const [loadingPerfis, setLoadingPerfis] = useState(false);
  const [activeTab, setActiveTab] = useState("dados");

  useEffect(() => {
    if (show) {
      setActiveTab("dados");
    }
  }, [show, usuario]);

  const { perfilAtivo } = useSelector((state) => state.access);
  const cidadeUsuario =
    perfilAtivo?.cidade_id ??
    (localStorage.getItem("cidadeUsuario")
      ? Number(localStorage.getItem("cidadeUsuario"))
      : null);

  /* PAGINAÇÃO VÍNCULOS */
  const [pageVinculos, setPageVinculos] = useState(0);
  const [sizeVinculos] = useState(5);
  const [totalPagesVinculos, setTotalPagesVinculos] = useState(0);

  useEffect(() => {
    const carregarUsuarioLogado = async () => {
      const { data } = await api.get("/api/user/show");
      setUsuarioLogado(data.user);
    };

    carregarUsuarioLogado();
  }, []);

  const tipoPerfilAtivo = Number(perfilAtivo?.tipo);

  const isAdministrador = tipoPerfilAtivo === 5;

  useEffect(() => {
    if (!usuario || !usuarioLogado || !perfilAtivo) return;

    let tipoFinal = usuario.tipo;
    let ibgeFinal = usuario.ibge || cidadeUsuario;

    //  se tipo for null → vira USUARIO (6)
    if (tipoFinal == null) {
      tipoFinal = 6;
    }

    //  se não tiver município → usa do logado
    if (!ibgeFinal) {
      ibgeFinal = cidadeUsuario;
    }

    // se NÃO for admin, força município do logado
    if (!isAdministrador) {
      tipoFinal = 6;
      ibgeFinal = cidadeUsuario;
    }

    setFormData({
      ...usuario,
      nome: usuario.nome,
      ativo: usuario.ativo ?? false,
      tipo: Number(tipoFinal),
      ibge: ibgeFinal ? Number(ibgeFinal) : null,
      cpf: usuario.cpf ? maskCpf(usuario.cpf) : "",
    });

    listarGrupos();
    listarVinculosUsuario();
    listarCidades();
    listarTiposAcesso();
    listarPerfisUsuario();
  }, [usuario, usuarioLogado, perfilAtivo]);

  /* LISTAR CIDADES */
  const listarCidades = async () => {
    try {
      setLoadingCidades(true);
      const { data } = await api.get("/api/tracking/cidade/sp");

      setCidadesOptions(
        (data || []).map((c) => ({
          value: c.id,
          label: c.nome,
        })),
      );
    } catch {
      Swal.fire("Erro", "Falha ao carregar cidades", "error");
    } finally {
      setLoadingCidades(false);
    }
  };

  /* LISTAR TIPOS DE ACESSO */
  const listarTiposAcesso = async () => {
    try {
      setLoadingTiposAcesso(true);

      const { data } = await api.get("/api/tracking/usuario/tipo-acesso");

      setTiposAcessoOptions(
        (data || []).map((t) => ({
          value: t.id,
          label: t.tipo,
        })),
      );
    } catch {
      Swal.fire("Erro", "Falha ao carregar tipos de acesso", "error");
    } finally {
      setLoadingTiposAcesso(false);
    }
  };

  /* LISTAR GRUPOS */
  const listarGrupos = async () => {
    try {
      setLoadingGrupos(true);

      const params = isAdministrador
        ? { page: 0, size: 300, sort: "cidade" }
        : { userId: usuario.id, page: 0, size: 300, sort: "cidade" };

      const { data } = await api.get("/api/tracking/grupos-acesso", { params });

      setGruposOptions(
        (data.content || []).map((g) => ({
          value: g.id,
          label: `${g.nome} (${g.cidade})`,
        })),
      );
    } catch {
      Swal.fire("Erro", "Falha ao carregar grupos", "error");
    } finally {
      setLoadingGrupos(false);
    }
  };

  /* VINCULAR */
  const handleVincular = async () => {
    const novos = gruposSelecionados.filter(
      (g) => !vinculados.some((v) => v.grupoAcessoId === g.value),
    );

    if (novos.length === 0) return;

    try {
      await Promise.all(
        novos.map((g) =>
          api.post("/api/tracking/usuario-grupo-acesso", {
            userId: usuario.id,
            grupoAcessoId: g.value,
          }),
        ),
      );

      Swal.fire("Vinculado!", "Grupo vinculado com sucesso", "success");
      setGruposSelecionados([]);
      setPageVinculos(0);
      listarVinculosUsuario(0);
    } catch {
      Swal.fire("Erro", "Falha ao vincular grupo", "error");
    }
  };

  /* LSITAR VINCULO USUÁRIO */
  const listarVinculosUsuario = async () => {
    if (!usuario?.id) return;

    try {
      setLoadingVinculos(true);

      const { data } = await api.get(
        `/api/tracking/usuario-grupo-acesso/usuario/${usuario.id}`,
        {
          params: {
            page: pageVinculos,
            size: sizeVinculos,
            sort: "grupoAcessoId",
          },
        },
      );

      setVinculados(data.content || []);
      setTotalPagesVinculos(data.totalPages || 0);
    } catch {
      Swal.fire("Erro", "Falha ao carregar vínculos", "error");
    } finally {
      setLoadingVinculos(false);
    }
  };

  useEffect(() => {
    if (activeTab === "grupos") {
      listarVinculosUsuario();
    }
  }, [pageVinculos, activeTab]);

  const irPaginaVinculos = (novaPagina) => {
    if (novaPagina >= 0 && novaPagina < totalPagesVinculos) {
      setPageVinculos(novaPagina);
    }
  };

  /* ATUALIZAR USUÁRIO */
  const handleAtualizarUsuario = async () => {
    const { valid, message } = validateCpfWithMessage(formData.cpf);

    if (!valid) {
      Swal.fire("Atenção", message, "warning");
      return;
    }

    try {
      const payload = {
        id: usuario.id,
        name: formData.nome,
        email: formData.email,
        estabelecimento: formData.estabelecimento,
        ibge: formData.ibge ?? usuario.ibge ?? cidadeUsuario ?? usuarioLogado?.ibge,
        status: formData.ativo,
        cpf: formData.cpf.replace(/\D/g, ""),
        tipoDescricao: formData.tipoDescricao,
        tipo: formData.tipo,
      };

      console.log("[EditUserModal] payload update usuario", {
        usuarioId: usuario.id,
        formData,
        payload,
      });

      await api.put(`/api/tracking/usuario/atualizar/${usuario.id}`, payload);

      Swal.fire("Sucesso", "Usuário atualizado com sucesso", "success");

      onUpdated();

      //   onClose();
    } catch {
      Swal.fire("Erro", "Falha ao atualizar usuário", "error");
    }
  };

  /*  REMOVER */
  const removerVinculo = async (grupoAcessoId) => {
    const confirmar = await Swal.fire({
      title: "Remover vínculo?",
      icon: "warning",
      showCancelButton: true,
    });

    if (!confirmar.isConfirmed) return;

    try {
      await api.delete("/api/tracking/usuario-grupo-acesso", {
        params: { userId: usuario.id, grupoAcessoId },
      });

      Swal.fire("Removido!", "Vínculo removido", "success");
      setPageVinculos(0);
      listarVinculosUsuario(0);
    } catch {
      Swal.fire("Erro", "Falha ao remover vínculo", "error");
    }
  };

  /*  LISTAR PERFIL DE USUARIO */
  const listarPerfisUsuario = async () => {
    if (!usuario?.id) return;

    try {
      setLoadingPerfis(true);

      const { data } = await api.get(
        `/api/tracking/api/perfis/usuario/${usuario.id}`,
      );

      setPerfis(data || []);
    } catch (error) {
      //  TRATAMENTO CORRETO
      if (error?.response?.status === 404) {
        setPerfis([]);
      } else {
        Swal.fire("Erro", "Falha ao carregar perfis de acesso", "error");
      }
    } finally {
      setLoadingPerfis(false);
    }
  };

  /*  INCLUIR NOVO PERFIL DE USUARIO */
  const handleIncluirPerfil = async () => {
    if (!formData.tipo || !formData.ibge) {
      Swal.fire(
        "Atenção",
        "Selecione o tipo de acesso e o município",
        "warning",
      );
      return;
    }

    try {
      const payload = {
        userId: usuario.id,
        tipoAcessoId: formData.tipo,
        descricaoAcesso: formData.tipoDescricao,
        cidadeId: formData.ibge,
        municipio:
          cidadesOptions.find((c) => c.value === formData.ibge)?.label || "",
      };

      await api.post(`/api/tracking/api/perfis`, payload);

      Swal.fire("Sucesso", "Acesso incluído com sucesso", "success");

      listarPerfisUsuario();
    } catch {
      Swal.fire("Erro", "Falha ao incluir tipo de acesso", "error");
    }
  };

  /*  EXCLUIR NOVO PERFIL DE USUARIO */
  const removerPerfil = async (id) => {
    const confirmar = await Swal.fire({
      title: "Remover acesso?",
      text: "Essa ação não poderá ser desfeita",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
    });

    if (!confirmar.isConfirmed) return;

    try {
      await api.delete(`/api/tracking/api/perfis/${id}`);

      Swal.fire("Removido", "Acesso removido com sucesso", "success");

      listarPerfisUsuario();
    } catch {
      Swal.fire("Erro", "Falha ao remover acesso", "error");
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal show d-block"
      style={{ background: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-xl modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h4>Configuração do Usuário</h4>
            <button className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            <ul className="nav nav-tabs tab-style-1 d-sm-flex d-block">
              <li className="nav-item">
                <Link
                  className="nav-link active"
                  aria-current="page"
                  data-bs-toggle="tab"
                  to="#dados"
                >
                  Dados do Usuário
                </Link>
              </li>

              <li className="nav-item">
                <Link className="nav-link" data-bs-toggle="tab" to="#grupos">
                  Grupos de Acesso
                </Link>
              </li>

              <li className="nav-item">
                <Link className="nav-link" data-bs-toggle="tab" to="#acessos">
                  Tipo de acesso
                </Link>
              </li>
            </ul>

            <div className="tab-content mt-3">
              {/*  TAB DADOS  */}
              <div className="tab-pane fade show active" id="dados">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Nome</label>
                    <input
                      className="form-control"
                      value={formData.nome || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, nome: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">E-mail</label>
                    <input
                      className="form-control"
                      value={formData.email || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">CPF</label>

                    <input
                      className={`form-control ${cpfError ? "is-invalid" : ""}`}
                      value={formData.cpf || ""}
                      maxLength={14}
                      onChange={(e) => {
                        const value = maskCpf(e.target.value);

                        setFormData({
                          ...formData,
                          cpf: value,
                        });

                        const result = validateCpfWithMessage(value);
                        setCpfError(result.valid ? "" : result.message);
                      }}
                    />

                    {cpfError && (
                      <small className="text-danger">{cpfError}</small>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label>Status do Usuário</label>
                    <div className="form-check form-switch mt-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!formData.ativo}
                        onChange={() =>
                          setFormData({ ...formData, ativo: !formData.ativo })
                        }
                      />
                      <label className="form-check-label ms-2">
                        {formData.ativo ? "Ativo" : "Inativo"}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/*  TAB GRUPOS  */}
              <div className="tab-pane fade" id="grupos">
                <h6 className="mb-2">Grupos Disponíveis</h6>

                <Select
                  isMulti
                  styles={{
                    ...reactSelectStyles,
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  options={gruposOptions}
                  value={gruposSelecionados}
                  onChange={setGruposSelecionados}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  placeholder={
                    loadingGrupos ? "Carregando..." : "Selecione grupos..."
                  }
                />

                <button
                  className="btn btn-primary my-3"
                  onClick={handleVincular}
                  disabled={gruposSelecionados.length === 0}
                >
                  <i className="feather-link me-2"></i>
                  Vincular
                </button>

                <h6 className="mb-2">Grupos Vinculados</h6>

                <div className="table-responsive">
                  <table className="table table-sm table-bordered table-hover align-middle">
                    <thead className="thead-light">
                      <tr>
                        <th>Grupo</th>
                        <th width="40"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingVinculos && (
                        <tr>
                          <td className="text-muted">Carregando...</td>
                        </tr>
                      )}

                      {!loadingVinculos &&
                        vinculados.map((v) => (
                          <tr key={v.grupoAcessoId}>
                            <td>
                              {v.grupoAcessoId} - {v.grupoAcessoNome}
                            </td>
                            <td width="40">
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => removerVinculo(v.grupoAcessoId)}
                              >
                                <i className="fas fa-trash" />
                              </button>
                            </td>
                          </tr>
                        ))}

                      {!loadingVinculos && vinculados.length === 0 && (
                        <tr>
                          <td className="text-muted">Nenhum vínculo</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {/* PAGINAÇÃO VÍNCULOS */}
                  {totalPagesVinculos > 1 && (
                    <div className="d-flex justify-content-between mt-2">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        disabled={pageVinculos === 0}
                        onClick={() => irPaginaVinculos(pageVinculos - 1)}
                      >
                        ← Anterior
                      </button>

                      <span>
                        Página {pageVinculos + 1} de {totalPagesVinculos}
                      </span>

                      <button
                        className="btn btn-outline-secondary btn-sm"
                        disabled={pageVinculos + 1 >= totalPagesVinculos}
                        onClick={() => irPaginaVinculos(pageVinculos + 1)}
                      >
                        Próxima →
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/*  TAB ACESSOS  */}
              <div className="tab-pane fade" id="acessos">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Tipo de Acesso</label>
                    <Select
                      styles={reactSelectStyles}
                      options={tiposAcessoOptions}
                      isDisabled={!isAdministrador}
                      isClearable
                      value={
                        tiposAcessoOptions.find(
                          (t) => t.value === formData.tipo,
                        ) || null
                      }
                      onChange={(selected) => {
                        setFormData((prev) => ({
                          ...prev,
                          tipo: selected?.value ?? null,
                          tipoDescricao: selected?.label ?? null,
                        }));
                      }}
                      placeholder={
                        loadingTiposAcesso
                          ? "Carregando tipos..."
                          : "Selecione o tipo de acesso"
                      }
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Cidade</label>
                    <Select
                      styles={reactSelectStyles}
                      options={cidadesOptions}
                      isClearable
                      isDisabled={!isAdministrador}
                      value={
                        cidadesOptions.find(
                          (c) => Number(c.value) === Number(formData.ibge),
                        ) || null
                      }
                      onChange={(selected) =>
                        setFormData({
                          ...formData,
                          ibge: selected?.value,
                        })
                      }
                      placeholder={
                        loadingCidades
                          ? "Carregando municípios..."
                          : "Selecione o município"
                      }
                    />
                  </div>
                </div>

                <button
                  className="btn btn-primary mt-0 my-3"
                  onClick={handleIncluirPerfil}
                  disabled={!formData.tipo || !formData.ibge}
                >
                  <i className="feather-link me-2"></i>
                  Vincular
                </button>

                <h6 className="mb-2">Acessos Vinculados</h6>

                <div className="table-responsive">
                  <table className="table table-sm table-bordered table-hover align-middle">
                    <thead className="thead-light">
                      <tr>
                        <th>Tipo de acesso</th>
                        <th>Cidade</th>
                        <th width="40"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {loadingPerfis && (
                        <tr>
                          <td colSpan="3" className="text-muted">
                            Carregando...
                          </td>
                        </tr>
                      )}

                      {!loadingPerfis &&
                        perfis.map((p) => (
                          <tr key={p.id}>
                            <td>
                              {p.tipoAcessoId} - {p.descricaoAcesso}
                            </td>
                            <td>
                              {p.cidadeId} - {p.municipio}
                            </td>
                            <td width="40">
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => removerPerfil(p.id)}
                              >
                                <i className="fas fa-trash" />
                              </button>
                            </td>
                          </tr>
                        ))}

                      {!loadingPerfis && perfis.length === 0 && (
                        <tr>
                          <td colSpan="3" className="text-muted">
                            Nenhum acesso vinculado
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer border-top d-grid gap-2 d-md-flex justify-content-end">
            <button className="btn btn-secondary" onClick={onClose}>
              <i className="feather-x me-1"></i>
              Fechar
            </button>

            {activeTab === "dados" && (
              <button
                className="btn btn-primary"
                onClick={handleAtualizarUsuario}
              >
                <i className="feather-save me-2"></i>
                Salvar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

EditUserModal.propTypes = {
  show: PropTypes.bool,
  usuario: PropTypes.object,
  onClose: PropTypes.func,
  onUpdated: PropTypes.func,
};

export default EditUserModal;
